import { motion } from "framer-motion";
import { MapPin, Mail } from "lucide-react";

const locations = [
  {
    city: "Abuja",
    tagline: "Elegance in the Capital",
    description: "Discover sophisticated luxury in Nigeria's political and cultural hub",
    highlights: ["Asokoro", "Central Business District"],
    image: "/abuja-cityscape.jpg",
    country: "Nigeria",
    address: "Asokoro District, Abuja",
    email: "abuja@raha-iduu.com",
  },
  {
    city: "Lagos",
    tagline: "The City That Never Sleeps",
    description: "Experience the vibrant energy of Africa's entertainment capital",
    highlights: ["Victoria Island", "Ikeja"],
    image: "/lagos-skyline.jpg",
    country: "Nigeria",
    address: "Victoria Island, Lagos",
    email: "lagos@raha-iduu.com",
  },
  {
    city: "Calabar",
    tagline: "Where Culture Meets Celebration",
    description: "Immerse yourself in the heart of carnival and coastal luxury",
    highlights: ["Carnival District", "Marina Resort"],
    image: "/calabar-carnival.jpg",
    country: "Nigeria",
    address: "Marina Resort, Calabar",
    email: "calabar@raha-iduu.com",
  }
];

export default function Locations() {
  return (
    <section className="relative py-16 sm:py-20 md:py-24 lg:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-luxury-navy" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16 lg:mb-20"
        >
          <span className="text-luxury-gold uppercase tracking-[0.3em] text-xs sm:text-sm mb-4 block">
            Where We Serve
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-gradient">Three Cities, One Vision</span>
          </h2>
          <p className="text-luxury-warmGray text-sm sm:text-base lg:text-lg max-w-2xl mx-auto px-4">
            Premium concierge services across Nigeria's most dynamic destinations
          </p>
        </motion.div>

        {/* Locations Grid */}
        <div className="space-y-12 sm:space-y-16">
          {locations.map((location, index) => (
            <motion.div
              key={location.city}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className={`flex flex-col ${
                index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
              } gap-6 sm:gap-8 items-center`}
            >
              {/* Image */}
              <div className="lg:w-1/2 relative group">
                <div className="aspect-[4/3] bg-luxury-deepPurple luxury-border overflow-hidden relative">
                  <img
                    src={location.image}
                    alt={`${location.city} cityscape`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=600&h=450&fit=crop&auto=format&dpr=2";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-luxury-gold/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" />
                </div>
                <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 w-16 h-16 sm:w-24 sm:h-24 border border-luxury-gold/30 pointer-events-none" />
              </div>

              {/* Content */}
              <div className="lg:w-1/2 space-y-4 sm:space-y-6">
                <div>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-luxury-cream mb-2">
                    {location.city}
                  </h3>
                  <p className="text-luxury-gold italic text-base sm:text-lg mb-3 sm:mb-4">
                    {location.tagline}
                  </p>
                  <p className="text-luxury-warmGray text-sm sm:text-base lg:text-lg leading-relaxed">
                    {location.description}
                  </p>
                </div>

                {/* Highlights */}
                <div>
                  <h4 className="text-luxury-gold uppercase tracking-wider text-xs sm:text-sm mb-2 sm:mb-3">
                    Key Areas
                  </h4>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {location.highlights.map((highlight) => (
                      <span
                        key={highlight}
                        className="px-3 sm:px-4 py-1 sm:py-2 luxury-border text-luxury-warmGray text-xs sm:text-sm hover:bg-luxury-gold/10 transition-all duration-300"
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="pt-4 sm:pt-6 border-t border-luxury-gold/20 space-y-2 sm:space-y-3">
                  <div className="flex items-center gap-2 sm:gap-3 text-luxury-warmGray">
                    <MapPin size={16} className="text-luxury-gold flex-shrink-0" />
                    <span className="text-xs sm:text-sm">{location.address}</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 text-luxury-warmGray">
                    <Mail size={16} className="text-luxury-gold flex-shrink-0" />
                    <span className="text-xs sm:text-sm">{location.email}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
